import { AxiosResponse } from "axios";
import CatchBlockFunction from "../../../../components/hooks/CatchBlockFunction";
import {
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { toast } from "sonner";
import { get, patch, post, putType } from "../../../../utils/request";
import {
  selectFormData,
  selectOrderBy,
  selectOrderDir,
  selectPageNo,
  selectPageSize,
  selectSearch,
  selectToggleLoading,
} from "./selector";

import { IData } from "../types";
import { actions } from "./slice";
import { packageValidation } from "../../../../components/Common/Validations";
import { t } from "i18next";
import { selectGroupPackage } from "../../group/redux/selector";
import { IGroupData } from "../../group/types";

export function* doCreateRequest(action: {
  payload: { callback: () => void };
}) {
  yield delay(500);

  const form: IData = yield select(selectFormData);
  const isValid = packageValidation(form);
  if (!isValid) {
    return;
  }
  const data = {
    title: form.title,
    code: form.code,
    subCategoryId: form.subCategoryId,
    type: form.type,
    price: Number(form.price),
    taxRate: Number(form.taxRate),
    status: form.status,
    items: form.items,
    allergicImages: form?.allergicImages,
    stickers: form?.stickers,
    images: form?.images ?? [],
    featuredImage: form?.featuredImage ?? [],
    shipmentCapacity: Number(form.shipmentCapacity),
  };
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(post, `admin/package/add`, data);
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback);
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetListRequest() {
  yield delay(500);

  try {
    const pageNo: number = yield select(selectPageNo);
    const pageSize: number = yield select(selectPageSize);
    const orderBy: string = yield select(selectOrderBy);
    const orderDir: string = yield select(selectOrderDir);
    const search: string = yield select(selectSearch);
    const buttonLoading: boolean = yield select(selectToggleLoading);
    if (!buttonLoading) {
      yield put(actions.setLoading(true));
    }

    const response: AxiosResponse = yield call(
      get,
      `admin/package/list?pageNo=${pageNo}&pageSize=${pageSize}${
        search.length > 0 ? "&search=" + search : ""
      }${orderBy.length > 0 ? "&orderBy=" + orderBy : ""}${
        orderDir.length > 0 ? "&orderDirection=" + orderDir : ""
      }`
    );
    yield put(actions.setLoading(false));
    yield put(actions.setToggleLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(
      actions.setTotalRow(
        response.data.data.meta ? response.data.data.meta.total : 0
      )
    );
    yield put(actions.setList(response.data.data.results));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    yield put(actions.setToggleLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetByIdRequest(action: { payload: string }) {
  yield delay(500);

  try {
    yield put(actions.setLoading(true));
    const response: AxiosResponse = yield call(
      get,
      `admin/package/get/${
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(actions.setDataForm(response.data.data));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doUpdateRequest(action: {
  payload: { callback: () => void };
}) {
  yield delay(500);

  const form: IData = yield select(selectFormData);
  const groupList: IGroupData[] = yield select(selectGroupPackage);
  const isValid = packageValidation(form);
  if (!isValid) {
    return;
  }
  const items = form.items.filter(
    (x) => groupList.findIndex((y) => y._id === x.groupId) !== -1
  );
  const data = {
    title: form.title,
    code: form.code,
    subCategoryId: form.subCategoryId,
    type: form.type,
    price: Number(form.price),
    taxRate: Number(form.taxRate),
    status: form.status,
    items: items,
    allergicImages: form?.allergicImages,
    stickers: form?.stickers,
    images: form?.images ?? [],
    featuredImage: form?.featuredImage ?? [],
    shipmentCapacity: Number(form.shipmentCapacity),
  };
  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      putType,
      `admin/package/update/${form._id}`,
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback);
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doUpdateStatusRequest(action: {
  payload: { id: string; callback: any };
}) {
  yield delay(500);
  try {
    yield put(actions.setButtonLoading(true));
    yield put(actions.setToggleLoading(true));
    const response: AxiosResponse = yield call(
      patch,
      `admin/package/status/update/${
        action.payload.id !== undefined && action.payload.id !== null
          ? action.payload.id
          : ""
      }`,
      {}
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action.payload.callback());
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetAllListRequest() {
  yield delay(500);

  try {
    const response: AxiosResponse = yield call(get, `admin/package/all`);
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(actions.setAllList(response.data.data));
  } catch (error: any) {
    CatchBlockFunction(error);
  }
}

export function* doCreateItemsRequest(action: {
  payload: { callback: () => void };
}) {
  yield delay(500);

  const form: IData = yield select(selectFormData);

  if (form?.itemsForm?.groupId?.length === 0) {
    toast.error(t("package.validation.items.group"));
    return;
  }
  if (form?.itemsForm?.productId?.length === 0) {
    toast.error(t("package.validation.items.product"));
    return;
  }
  if (form?.items?.length > 0) {
    const isDuplicate = form.items.some((item) => {
      if (item?.groupId === form?.itemsForm?.groupId) {
        toast.error(
          t("package.validation.items.alreadySelected", {
            groupTitle: item.groupTitle,
          })
        );
        return true;
      }
      return false;
    });

    if (isDuplicate) return;
  }

  const data = {
    groupId: form.itemsForm.groupId,
    groupTitle: form.itemsForm.groupTitle,
    productId: form.itemsForm.productId,
    productTitle: form.itemsForm.productTitle,
  };
  try {
    yield put(actions.setItemsLoading(true));
    yield put(actions.setItemsFromData(data));
    yield put(actions.setItemsLoading(false));

    toast.success("item created successfully");
    yield call(action?.payload?.callback);
  } catch (error: any) {
    yield put(actions.setItemsLoading(false));
    CatchBlockFunction(error);
  }
}
export function* packageRepoSaga() {
  yield takeLatest(actions.doCreate, doCreateRequest);
  yield takeLatest(actions.doUpdate, doUpdateRequest);
  yield takeLatest(actions.doGetList, doGetListRequest);
  yield takeLatest(actions.doGetById, doGetByIdRequest);
  yield takeLatest(actions.doUpdateStatus, doUpdateStatusRequest);
  yield takeLatest(actions.doGetAllPackageList, doGetAllListRequest);
  yield takeLatest(actions.doCreateItems, doCreateItemsRequest);
}
